if (!customElements.get("nabla-back-in-stock")) {
  class NablaBackInStock extends HTMLElement {
    constructor() {
      super();

      this.DEV = false;
      this.enableBackInStock = this.dataset.backinstockEnabled === "true";
      this.buttons = Array.from(this.querySelectorAll("[data-js-backinstock]"));
      this.variantId = this.dataset.variantId || null;
      this.productId = this.dataset.productId || null;
      this.customerEmail = this.dataset.customerEmail || null;
      this.unsubscribe = null;
      this.boundHandleClick = this.handleNotifyClick.bind(this);
      this.boundVariantChange = this.handleVariantChange.bind(this);

      this.#log("constructor", {enable: this.enableBackInStock, variant: this.variantId});
    }

    connectedCallback() {
      if (!this.enableBackInStock) {
        this.setAttribute("hidden", "");
        return;
      }

      this.buttons.forEach((button) => button.addEventListener("click", this.boundHandleClick));
      this.updateButtonVisibility(!this.hasAttribute("hidden"));

      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.boundVariantChange);
    }

    disconnectedCallback() {
      this.buttons.forEach((button) => button.removeEventListener("click", this.boundHandleClick));
      if (typeof this.unsubscribe === "function") {
        this.unsubscribe();
        this.unsubscribe = null;
      }
    }

    handleNotifyClick(event) {
      event.preventDefault();
      if (!this.variantId) {
        this.#log("No variantId available for notify click");
        return;
      }

      publish(PUB_SUB_EVENTS.backInStockRequest, {
        variantId: this.variantId,
        customerEmail: this.customerEmail,
      });
    }

    handleVariantChange(payload) {
      if (!payload || !payload.variantId) return;
      if (this.productId && payload.productId && String(payload.productId) !== String(this.productId)) return;

      this.variantId = String(payload.variantId);
      this.dataset.variantId = this.variantId;

      if (payload.productId) {
        this.productId = String(payload.productId);
        this.dataset.productId = this.productId;
      }

      const isAvailable = payload.available === true || payload.available === "true";

      if (isAvailable) {
        this.setAttribute("hidden", "");
      } else {
        this.removeAttribute("hidden");
      }

      this.updateButtonVisibility(!isAvailable);
      this.#log("Variant change handled", {
        variant: this.variantId,
        available: isAvailable,
      });
    }

    updateButtonVisibility(shouldShow) {
      this.buttons.forEach((button) => {
        if (shouldShow) {
          button.removeAttribute("hidden");
          button.removeAttribute("aria-hidden");
          button.removeAttribute("disabled");
        } else {
          button.setAttribute("hidden", "");
          button.setAttribute("aria-hidden", "true");
          button.setAttribute("disabled", "disabled");
        }
      });
    }

    #log() {
      if (this.DEV) console.log("[" + this.constructor.name + "]", ...arguments);
    }
  }
  customElements.define("nabla-back-in-stock", NablaBackInStock);
}

if (!customElements.get("nabla-klaviyo-form")) {
  class NablaKlaviyoForm extends HTMLElement {
    constructor() {
      super();
      this.DEV = true;
      this.#log("constructor");

      this.klaviyoListId = this.dataset.klaviyoListId || null;
      this.klaviyoApiKey = this.dataset.klaviyoApiKey || null;
      this.form = this.querySelector("form");
      this.closeButton = this.querySelector(".nabla-bis__close-button");
      this.contentWrapper = this.querySelector(".nabla-bis__content-wrapper");
      this.submitButton = this.querySelector(".nabla-bis__submit");
      this.submitDefaultLabel = this.submitButton?.textContent?.trim() || "";
      this.successMessage = this.dataset.successMessage;
      this.errorMessage = this.dataset.errorMessage;
      this.validationEmailMessage = this.dataset.validationEmailMessage;
      this.validationPrivacyMessage = this.dataset.validationPrivacyMessage;
      this.variantId = null;
      this.customerEmail = null;
      this.previousActiveElement = null;
      this.boundKeyHandler = this.handleKeyDown.bind(this);
      this.boundFocusHandler = this.handleFocusTrap.bind(this);
    }

    connectedCallback() {
      this.#log("connectedCallback");

      subscribe(PUB_SUB_EVENTS.backInStockRequest, (payload) => {
        this.#log("Received:", PUB_SUB_EVENTS.backInStockRequest);
        this.variantId = payload.variantId;
        this.customerEmail = payload.customerEmail || "";
        this.prefillEmailField(this.customerEmail);
        this.openPopup();
      });

      this.addEventListener("click", this.clickHandler);
    }

    disconnectedCallback() {
      this.removeEventListener("keydown", this.boundKeyHandler);
      this.removeEventListener("focusin", this.boundFocusHandler);
    }

    clickHandler(event) {
      if (this.contentWrapper && !this.contentWrapper.contains(event.target)) {
        this.closePopup();
        this.#log("Click out");
      }
      if (event.target.matches(".nabla-bis__close-button, .nabla-bis__close-button *")) {
        this.closePopup();
        this.#log("Click x");
      }
      if (event.target.matches(".nabla-bis__submit, .nabla-bis__submit *")) {
        this.buildData(event);
      }
    }

    async buildData(event) {
      event.preventDefault();

      const emailInput = this.form?.querySelector('input[type="email"]');
      const privacyCheckbox = this.form?.querySelector('input[name="privacy"]');
      const emailError = this.querySelector("#nabla-back-email-error");
      const privacyError = this.querySelector("#nabla-back-privacy-error");
      const statusMessage = this.querySelector("#nabla-bis-status");
      const emailInputValue = emailInput?.value?.trim() || "";
      const hasEmail = Boolean(emailInputValue || this.customerEmail);
      const privacyValid = privacyCheckbox ? privacyCheckbox.checked : true;

      // Reset error states
      if (emailInput) {
        emailInput.setAttribute("aria-invalid", "false");
      }
      if (privacyCheckbox) {
        privacyCheckbox.setAttribute("aria-invalid", "false");
      }
      if (emailError) emailError.textContent = "";
      if (privacyError) privacyError.textContent = "";

      if (!hasEmail || !privacyValid) {
        const validationMessage = !hasEmail ? this.validationEmailMessage : this.validationPrivacyMessage;
        
        if (!hasEmail && emailInput) {
          emailInput.setAttribute("aria-invalid", "true");
          if (emailError) emailError.textContent = this.validationEmailMessage;
        }
        if (!privacyValid && privacyCheckbox) {
          privacyCheckbox.setAttribute("aria-invalid", "true");
          if (privacyError) privacyError.textContent = this.validationPrivacyMessage;
        }

        if (this.submitButton) {
          this.submitButton.textContent = validationMessage;
          this.submitButton.removeAttribute("disabled");
        }
        if (!hasEmail && emailInput) {
          emailInput.focus();
        } else if (!privacyValid && privacyCheckbox) {
          privacyCheckbox.focus?.();
        }
        if (this.submitButton && this.submitDefaultLabel) {
          setTimeout(() => {
            this.submitButton.textContent = this.submitDefaultLabel;
          }, 3500);
        }
        return;
      }

      if (this.submitButton) {
        this.submitButton.setAttribute("disabled", "disabled");
      }

      let submissionSucceeded = false;

      try {
        if (!this.klaviyoApiKey) throw new Error("Klaviyo public API key is missing");
        if (!this.variantId) throw new Error("Variant ID is missing");

        const email = emailInputValue || this.customerEmail;
        if (!email) throw new Error("Email is missing");

        const payload = {
          data: {
            type: "back-in-stock-subscription",
            attributes: {
              channels: ["EMAIL"],
              profile: {
                data: {
                  type: "profile",
                  attributes: {email},
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: "catalog-variant",
                  id: "$shopify:::$default:::" + this.variantId,
                },
              },
            },
          },
        };
        this.#log("PAYLOAD", payload);

        const response = await fetch(
          `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.klaviyoApiKey}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              revision: "2025-10-15",
            },
            body: JSON.stringify(payload),
          },
        );

        if (response.ok) {
          this.#log("Response", response);
          submissionSucceeded = true;
          const statusMessage = this.querySelector("#nabla-bis-status");
          if (statusMessage) {
            statusMessage.textContent = this.successMessage;
          }
          if (this.submitButton) {
            this.submitButton.textContent = this.successMessage;
          }
        } else {
          const err = await response.json();
          console.error("Error response from Klaviyo:", err);
          const statusMessage = this.querySelector("#nabla-bis-status");
          if (statusMessage) {
            statusMessage.textContent = this.errorMessage;
          }
          if (this.submitButton) {
            this.submitButton.textContent = this.errorMessage;
          }
        }
      } catch (error) {
        console.error("Exception while subscribing", error);
        const statusMessage = this.querySelector("#nabla-bis-status");
        if (statusMessage) {
          statusMessage.textContent = this.errorMessage;
        }
        if (this.submitButton) {
          this.submitButton.textContent = this.errorMessage;
        }
      } finally {
        if (this.submitButton && !submissionSucceeded) {
          this.submitButton.removeAttribute("disabled");
        }

        setTimeout(() => {
          if (this.submitButton && this.submitDefaultLabel) {
            this.submitButton.textContent = this.submitDefaultLabel;
          }

          if (submissionSucceeded) {
            if (this.form) {
              this.form.reset();
            }
            if (emailInput) {
              emailInput.value = "";
              emailInput.setAttribute("aria-invalid", "false");
            }
            if (privacyCheckbox) {
              privacyCheckbox.checked = false;
              privacyCheckbox.setAttribute("aria-invalid", "false");
            }
            if (this.submitButton) {
              this.submitButton.removeAttribute("disabled");
            }
            // Clear error messages
            const emailError = this.querySelector("#nabla-back-email-error");
            const privacyError = this.querySelector("#nabla-back-privacy-error");
            if (emailError) emailError.textContent = "";
            if (privacyError) privacyError.textContent = "";
            this.closePopup();
          }
        }, 3500);
      }
    }

    prefillEmailField(email) {
      const emailInput = this.form?.querySelector('input[type="email"]');
      if (emailInput && email) {
        emailInput.value = email;
      }
    }

    openPopup() {
      if (this.hasAttribute("open")) {
        this.#log("Popup already open");
        return;
      }

      // Store the element that had focus before opening
      this.previousActiveElement = document.activeElement;

      this.setAttribute("open", "");
      this.setAttribute("aria-hidden", "false");
      
      // Add event listeners for accessibility
      this.addEventListener("keydown", this.boundKeyHandler);
      this.addEventListener("focusin", this.boundFocusHandler);

      // Focus management: focus first focusable element
      requestAnimationFrame(() => {
        const firstFocusable = this.getFirstFocusableElement();
        if (firstFocusable) {
          firstFocusable.focus();
        }
      });

      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";

      this.#log("Popup open");
    }

    closePopup() {
      if (!this.hasAttribute("open")) return;

      this.removeAttribute("open");
      this.setAttribute("aria-hidden", "true");

      // Remove event listeners
      this.removeEventListener("keydown", this.boundKeyHandler);
      this.removeEventListener("focusin", this.boundFocusHandler);

      // Restore body scroll
      document.body.style.overflow = "";

      // Restore focus to the element that opened the popup
      if (this.previousActiveElement && typeof this.previousActiveElement.focus === "function") {
        requestAnimationFrame(() => {
          this.previousActiveElement.focus();
        });
      }
      this.previousActiveElement = null;

      // Clear status messages
      const statusMessage = this.querySelector("#nabla-bis-status");
      if (statusMessage) statusMessage.textContent = "";

      this.#log("Popup close");
    }

    handleKeyDown(event) {
      // Close on ESC key
      if (event.key === "Escape" || event.keyCode === 27) {
        event.preventDefault();
        this.closePopup();
        return;
      }

      // Trap focus: if Tab is pressed on last element, focus first
      // If Shift+Tab is pressed on first element, focus last
      if (event.key === "Tab" || event.keyCode === 9) {
        const focusableElements = this.getFocusableElements();
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    }

    handleFocusTrap(event) {
      // If focus moves outside the dialog, bring it back
      if (!this.contentWrapper?.contains(event.target)) {
        const firstFocusable = this.getFirstFocusableElement();
        if (firstFocusable) {
          firstFocusable.focus();
        }
      }
    }

    getFocusableElements() {
      const selector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      return Array.from(this.contentWrapper?.querySelectorAll(selector) || []).filter(
        (el) => !el.hasAttribute("disabled") && !el.hasAttribute("hidden")
      );
    }

    getFirstFocusableElement() {
      const focusableElements = this.getFocusableElements();
      return focusableElements[0] || this.closeButton || this.form?.querySelector('input[type="email"]');
    }

    #log(...args) {
      if (this.DEV) console.log("[NablaKlaviyoForm]", ...args);
    }
  }

  customElements.define("nabla-klaviyo-form", NablaKlaviyoForm);
}
